import { useContext, useEffect, useRef, useState } from 'react';
import { TitleEntity } from '@/redux/slices/slice_types';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectTitlesById as selectTitlesByIdV2 } from '@/redux/selectors/titleV2Selectors';
import { selectTitleDataById } from '@/redux/selectors/titleSelectors';
import { selectVersionSelections } from '@/redux/selectors/orderPlacementSelectors';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { ConfigContext } from '@/contextAPI/configContext';
import useConfirmationPrompt from '@/components/_hooks/useConfirmationPrompt';
import { clearVersionSelections, setAllVersionSelectionsByType } from '@/redux/slices/orderPlacementSlice';

const MIXED_SEASON_MESSAGE =
  'New schedules can only be created for one season at a time. Would you like to clear your current selections and start over with a different season?';

const MIXED_VERSION_TYPE_MESSAGE =
  'New schedules can only be created for one version type at a time. Would you like to clear your current selections and start over with a different version type?';

const MIXED_SEASON_AND_VERSION_TYPE_MESSAGE =
  'New schedules can only be created for one season and one version type at a time. Would you like to clear your current selections and start over?';

/**
 * This hook watches selected title versions and detects invalid mixed selections:
 * - versions from different seasons (lineage[1])
 * - versions with different version_type
 *
 * If an invalid combination is detected, it opens a confirmation dialog.
 */
export const useIncompatibleVersionSelectionConfirmation = () => {
  const dispatch = useAppDispatch();
  const isTitleV2 = useContext(ConfigContext)?.titleConfig?.v2;
  const titlesById = isTitleV2 ? useAppSelector(selectTitlesByIdV2) : useAppSelector(selectTitleDataById);
  const versionSelections = useAppSelector(selectVersionSelections);

  const prevVersionSelectionsRef = useRef<(TitleEntity | TitleEntityV2)[]>([]);
  const addedToSelectionVersionsRef = useRef<(TitleEntity | TitleEntityV2)[]>([]);

  const [confirmationMessage, setConfirmationMessage] = useState(MIXED_SEASON_MESSAGE);

  const { dialog: incompatibleVersionSelectionConfirmation, setDialogVisible: setShowAddFromAnotherSeasonConfirmation } = useConfirmationPrompt({
    title: 'Invalid Selection',
    message: confirmationMessage,
    buttons: {
      cancelText: 'Keep Selections',
      submitText: 'Clear Selections'
    },
    onCancel: () => {
      // revert selected versions to the state before the user tried to add invalid versions
      dispatch(
        setAllVersionSelectionsByType({
          versions: addedToSelectionVersionsRef.current,
          titlesById,
          isSelected: true
        })
      );
    },
    onConfirm: () => {
      // remove all selections and keep only the versions user tried to add last
      dispatch(clearVersionSelections());
      dispatch(
        setAllVersionSelectionsByType({
          versions: addedToSelectionVersionsRef.current,
          titlesById,
          isSelected: false
        })
      );
    }
  });

  useEffect(
    function checkIfInvalidVersionsWereAdded() {
      if (versionSelections.length < 2) {
        prevVersionSelectionsRef.current = versionSelections;
        return;
      }

      const currVersionIds = new Set(versionSelections.map((v) => v.id));
      const prevVersionIds = new Set(prevVersionSelectionsRef.current.map((v) => v.id));

      const addedIds = new Set([...currVersionIds].filter((id) => !prevVersionIds.has(id)));

      // Skip if no new versions were added (e.g. user removed something)
      if (addedIds.size === 0) {
        prevVersionSelectionsRef.current = versionSelections;
        return;
      }

      const addedVersions = versionSelections.filter((v) => addedIds.has(v.id));

      const grandParentIds = new Set(versionSelections.map((v) => v.lineage?.[1]).filter(Boolean));
      const versionTypes = new Set(versionSelections.map((v) => v.version_type));

      const hasMultipleSeasons = grandParentIds.size > 1;
      const hasMultipleVersionTypes = versionTypes.size > 1;

      if (hasMultipleSeasons || hasMultipleVersionTypes) {
        if (hasMultipleSeasons && hasMultipleVersionTypes) {
          setConfirmationMessage(MIXED_SEASON_AND_VERSION_TYPE_MESSAGE);
        } else if (hasMultipleSeasons) {
          setConfirmationMessage(MIXED_SEASON_MESSAGE);
        } else {
          setConfirmationMessage(MIXED_VERSION_TYPE_MESSAGE);
        }

        addedToSelectionVersionsRef.current = addedVersions;
        setShowAddFromAnotherSeasonConfirmation(true);
      }

      prevVersionSelectionsRef.current = versionSelections;
    },
    [versionSelections, setShowAddFromAnotherSeasonConfirmation]
  );

  return { incompatibleVersionSelectionConfirmation };
};
